import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { CapicheLoggerService } from 'capiche-logger';

const STORAGE_KEY = 'ra-voice';

function rankVoice(v: SpeechSynthesisVoice): number {
  if (/natural/i.test(v.name)) return 0;
  if (/online/i.test(v.name)) return 1;
  if (/google.*uk.*female/i.test(v.name)) return 2;
  if (/google/i.test(v.name)) return 3;
  return 4;
}

@Injectable({ providedIn: 'root' })
export class VoicePreferenceService {
  private logger = new CapicheLoggerService('voice-preference.service:', false);
  private zone = inject(NgZone);

  private voiceListSubject = new BehaviorSubject<SpeechSynthesisVoice[]>([]);
  private selectedVoiceSubject = new BehaviorSubject<SpeechSynthesisVoice | null>(null);

  voiceList$ = this.voiceListSubject.asObservable();
  selectedVoice$ = this.selectedVoiceSubject.asObservable();

  constructor() {
    if (!('speechSynthesis' in window)) {
      this.logger.log('Web Speech not available');
      return;
    }

    const load = () => {
      const ranked = window.speechSynthesis
        .getVoices()
        .filter((v) => v.lang.startsWith('en'))
        .sort((a, b) => rankVoice(a) - rankVoice(b));

      this.voiceListSubject.next(ranked);

      const saved = localStorage.getItem(STORAGE_KEY);
      const match = saved ? ranked.find((v) => v.name === saved) : null;
      this.selectedVoiceSubject.next(match ?? ranked[0] ?? null);

      this.logger.log('voices loaded:', ranked.length, 'selected:', this.selectedVoiceSubject.value?.name);
    };

    window.speechSynthesis.onvoiceschanged = () => this.zone.run(load);
    load();
  }

  get selectedVoice(): SpeechSynthesisVoice | null {
    return this.selectedVoiceSubject.value;
  }

  get voiceList(): SpeechSynthesisVoice[] {
    return this.voiceListSubject.value;
  }

  setVoice(name: string): void {
    const voice = this.voiceListSubject.value.find((v) => v.name === name);
    if (!voice) return;
    localStorage.setItem(STORAGE_KEY, name);
    this.selectedVoiceSubject.next(voice);
    this.logger.log('setVoice:', name);
  }
}
